import { createBrowserRouter, redirect, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { lazy, Suspense, useState, useEffect } from 'react';
import { CircularProgress, Box, Typography, Button, Paper } from '@mui/material';
import { lazyWithRetry, LazyWrapper } from './components/common/LazyWrapper';
import { ErrorBoundary } from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import LazyLoadFallback from './components/LazyLoadFallback';
import { NavigationProvider } from './contexts/NavigationContext';

// Core components that should always be available (not lazy loaded)
import ResponsiveLayout from './components/layout/ResponsiveLayout';
import TopNavLayout from './components/layout/TopNavLayout';
import CustomerLayout from './components/layout/CustomerLayout';
import MarketplaceLayout from './components/layout/MarketplaceLayout';
import ResponsiveDashboard from './pages/ResponsiveDashboard';
import Login from './pages/Login';
import LoginForm from './components/auth/LoginForm';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import SetPassword from './pages/SetPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SystemAdminRoute from './components/auth/SystemAdminRoute';
import { customerThemeLight } from './theme/customer-theme';

// Layout selector component with NavigationProvider
const DynamicLayout = () => {
  const [navigationLayout, setNavigationLayout] = useState(
    localStorage.getItem('navigationLayout') || 'top'
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    // Listen for navigation layout changes
    const handleLayoutChange = (event: CustomEvent) => {
      const newLayout = event.detail?.layout || localStorage.getItem('navigationLayout') || 'top';
      setNavigationLayout(newLayout);
    };

    // Listen for window resize to detect mobile
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('navigationLayoutChange', handleLayoutChange as EventListener);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('navigationLayoutChange', handleLayoutChange as EventListener);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Always use ResponsiveLayout on mobile (< 900px) for consistent mobile experience
  const isMobile = windowWidth < 900;

  return (
    <NavigationProvider>
      {/* On mobile, always use ResponsiveLayout regardless of navigation preference */}
      {isMobile ? (
        <ResponsiveLayout />
      ) : (
        navigationLayout === 'top' ? <TopNavLayout /> : <ResponsiveLayout />
      )}
    </NavigationProvider>
  );
};

// Loading component for suspense fallbacks
const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress />
  </Box>
);

// Lazy load heavy components - Primary pages (high frequency access)
const Inventory = lazyWithRetry(() => import('./pages/Inventory'), 3, () => <LazyLoadFallback pageName="Inventory" />);
const InventoryEnhanced = lazyWithRetry(() => import('./pages/InventoryEnhanced'), 3, () => <LazyLoadFallback pageName="Enhanced Inventory" />);
const ConsignmentVehicles = lazyWithRetry(() => import('./pages/ConsignmentVehicles'), 3, () => <LazyLoadFallback pageName="Consignment Vehicles" />);
const Customers = lazyWithRetry(() => import('./pages/Customers'), 3, () => <LazyLoadFallback pageName="Customers" />);
const Sales = lazyWithRetry(() => import('./pages/Sales'), 3, () => <LazyLoadFallback pageName="Sales" />);

// Lazy load heavy components - Vehicle management
const AddVehicle = lazyWithRetry(() => import('./pages/AddVehicle'));
const VehicleDetail = lazyWithRetry(() => import('./pages/VehicleDetail'));
const VehiclePrintPage = lazyWithRetry(() => import('./pages/VehiclePrintPage'));
const EditVehicle = lazyWithRetry(() => import('./pages/EditVehicle'));

// Lazy load heavy components - Customer management
const CustomerDetail = lazyWithRetry(() => import('./pages/CustomerDetail'));
const CustomerDetailEnhanced = lazyWithRetry(() => import('./pages/CustomerDetailEnhanced'));
const EditCustomer = lazyWithRetry(() => import('./pages/EditCustomer'));

// Lazy load heavy components - Sales management
const AddDealApple = lazyWithRetry(() => import('./pages/AddDealApple'));
const DealDetail = lazyWithRetry(() => import('./pages/DealDetail'));
const DealDetailNew = lazyWithRetry(() => import('./pages/DealDetailNew'));
const CustomerSignature = lazyWithRetry(() => import('./pages/CustomerSignature'));
const CustomerSignatureWizard = lazyWithRetry(() => import('./pages/CustomerSignatureWizard'));
const SignDealPage = lazyWithRetry(() => import('./pages/SignDealPage'));
const SignTemplatesAdmin = lazyWithRetry(() => import('./pages/admin/SignTemplatesAdmin'));
const CustomerChat = lazyWithRetry(() => import('./pages/public/CustomerChat'));

// Lazy load heavy components - Analytics and Intelligence (chart heavy)
const Reports = lazyWithRetry(() => import('./pages/Reports'));
const StandardReportsPage = lazyWithRetry(() => import('./pages/StandardReportsPage'));
const ManagementReportsPage = lazyWithRetry(() => import('./pages/ManagementReportsPage'));
const IntelligenceHub = lazyWithRetry(() => import('./pages/IntelligenceHub'));
const IntelligenceHubPalantir = lazyWithRetry(() => import('./pages/IntelligenceHubPalantir'));
const Sourcing = lazyWithRetry(() => import('./pages/Sourcing'));

// Lazy load heavy components - AI and Tools
const DealerAIClaude = lazyWithRetry(() => import('./pages/DealerAIClaude'));
const TestInventory = lazyWithRetry(() => import('./test-inventory').then(m => ({ default: m.TestInventory })));
const PartsFinder = lazyWithRetry(() => import('./pages/PartsFinder'));
const EasyCarsDocumentation = lazyWithRetry(() => import('./pages/EasyCarsDocumentation'));
const Dialer = lazyWithRetry(() => import('./pages/DialerSimple'), 3, () => <LazyLoadFallback pageName="Dialer" />);
const Inbox = lazyWithRetry(() => import('./pages/Inbox'), 3, () => <LazyLoadFallback pageName="Inbox" />);

// Lazy load heavy components - Calendar and Workshop (react-big-calendar heavy)
const Calendar = lazyWithRetry(() => import('./pages/Calendar'));
const Workshop = lazyWithRetry(() => import('./pages/WorkshopSimple'));

// Lazy load heavy components - Document heavy (PDF generation)
const Documents = lazyWithRetry(() => import('./pages/Documents'));

// Lazy load secondary pages
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Help = lazyWithRetry(() => import('./pages/Help'));
const TipsAndPosts = lazyWithRetry(() => import('./pages/TipsAndPosts'));
const GettingStarted = lazyWithRetry(() => import('./pages/GettingStarted'));
const UserManagement = lazyWithRetry(() => import('./pages/UserManagement'));
const Tasks = lazyWithRetry(() => import('./pages/Tasks'));
const TasksSimple = lazyWithRetry(() => import('./pages/TasksSimple'));
const DealerDispatch = lazyWithRetry(() => import('./pages/DealerDispatch'));
const DealerDispatchEnhanced = lazyWithRetry(() => import('./pages/DealerDispatchEnhanced'));
const JobsApple = lazyWithRetry(() => import('./pages/JobsApple'));
const Credits = lazyWithRetry(() => import('./pages/Credits'));
const Accounting = lazyWithRetry(() => import('./pages/Accounting'));
const GovernmentPayments = lazyWithRetry(() => import('./pages/GovernmentPayments'));
const PurchaseOrders = lazyWithRetry(() => import('./pages/PurchaseOrders'));
const PurchaseOrderDetail = lazyWithRetry(() => import('./pages/PurchaseOrderDetail'));
const Commission = lazyWithRetry(() => import('./pages/Commission'));
const SalesTools = lazyWithRetry(() => import('./pages/SalesTools'));
const Advertising = lazyWithRetry(() => import('./pages/Advertising'));
const PPSRReports = lazyWithRetry(() => import('./pages/PPSRReports'));
const OverheadExpenses = lazyWithRetry(() => import('./pages/OverheadExpenses'));
const Expenses = lazyWithRetry(() => import('./pages/Expenses'));
const DealingsRegister = lazyWithRetry(() => import('./pages/DealingsRegister'));
const LogOfChanges = lazyWithRetry(() => import('./pages/LogOfChanges'));
const TollNotice = lazyWithRetry(() => import('./pages/TollNotice'));
const RegistrationTransfer = lazyWithRetry(() => import('./pages/RegistrationTransfer'));
const RegistrationRenewal = lazyWithRetry(() => import('./pages/RegistrationRenewal'));
const BulkPricingUpdate = lazyWithRetry(() => import('./pages/BulkPricingUpdate'));
const DealerLicences = lazyWithRetry(() => import('./pages/DealerLicences'));
const UserProfile = lazyWithRetry(() => import('./pages/UserProfile'));
const AccountManagement = lazyWithRetry(() => import('./pages/AccountManagement'));
const Contacts = lazyWithRetry(() => import('./pages/Contacts'));
const BackupManager = lazyWithRetry(() => import('./components/BackupManager'));
const AdminTokenUsage = lazyWithRetry(() => import('./pages/AdminTokenUsageImproved'));
const BankAccounts = lazyWithRetry(() => import('./pages/BankAccounts'));
const Subscription = lazyWithRetry(() => import('./pages/Subscription'));

// Lazy load test and utility pages
const TestConnection = lazyWithRetry(() => import('./pages/TestConnection'));
const SimpleTest = lazyWithRetry(() => import('./pages/SimpleTest'));
const TestFormProtection = lazyWithRetry(() => import('./pages/TestFormProtection'));
const TestModalProtection = lazyWithRetry(() => import('./pages/TestModalProtection'));
const VehicleTest = lazyWithRetry(() => import('./pages/VehicleTest'));
const TestFeatures = lazyWithRetry(() => import('./pages/TestFeatures'));
const AutograbTest = lazyWithRetry(() => import('./pages/AutograbTest'));
const AutograbTestPage = lazyWithRetry(() => import('./pages/AutograbTestPage'));

// Lazy load migration pages
const MigrationHub = lazyWithRetry(() => import('./pages/migrations/MigrationHub'));
const VirtualYardMigration = lazyWithRetry(() => import('./pages/migrations/VirtualYardMigration'));
const EasyCarsMigration = lazyWithRetry(() => import('./pages/migrations/EasyCarsMigration'));
const ExcelMigration = lazyWithRetry(() => import('./pages/migrations/ExcelMigration'));

// Lazy load customer interaction pages
const ScheduleTestDrive = lazyWithRetry(() => import('./pages/ScheduleTestDrive'));
const AddVehicleInterest = lazyWithRetry(() => import('./pages/AddVehicleInterest'));

// Lazy load Xero integration pages
const XeroCallback = lazyWithRetry(() => import('./pages/XeroCallback'));
const XeroTest = lazyWithRetry(() => import('./pages/XeroTest'));
const XeroDebug = lazyWithRetry(() => import('./pages/XeroDebug'));
const XeroDebugCredentials = lazyWithRetry(() => import('./pages/XeroDebugCredentials'));
const XeroFix = lazyWithRetry(() => import('./pages/XeroFix'));

// Lazy load marketplace pages
const Homepage = lazyWithRetry(() => import('./pages/marketplace/Homepage'));
const BrowseCars = lazyWithRetry(() => import('./pages/marketplace/BrowseCars'));
const MarketplaceVehicleDetail = lazyWithRetry(() => import('./pages/marketplace/VehicleDetail'));
const SellYourCar = lazyWithRetry(() => import('./pages/marketplace/SellYourCar'));
const CarFinance = lazyWithRetry(() => import('./pages/marketplace/CarFinance'));
const HowItWorks = lazyWithRetry(() => import('./pages/marketplace/HowItWorks'));
const Contact = lazyWithRetry(() => import('./pages/marketplace/Contact'));
const About = lazyWithRetry(() => import('./pages/marketplace/About'));

// Lazy load customer-facing pages (legacy)
const CustomerLanding = lazyWithRetry(() => import('./pages/CustomerLanding'));
const BrowsePage = lazyWithRetry(() => import('./pages/BrowsePage'));
const VehicleDetailPage = lazyWithRetry(() => import('./pages/VehicleDetailPage'));
const TestDrivePage = lazyWithRetry(() => import('./pages/TestDrivePage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const CustomerRedirect = lazyWithRetry(() => import('./pages/CustomerRedirect'));
const DemoPage = lazyWithRetry(() => import('./pages/DemoPage'));
const SignUpPage = lazyWithRetry(() => import('./pages/SignUpPage'));

// Lazy load internal admin pages
const OnboardingSubmissions = lazyWithRetry(() => import('./pages/internal/OnboardingSubmissions'));
const InternalPricing = lazyWithRetry(() => import('./pages/InternalPricing'));
const AdminDashboard = lazyWithRetry(() => import('./pages/internal/AdminDashboard'));
const RepCalculator = lazyWithRetry(() => import('./pages/internal/RepCalculator'));
const Playbook = lazyWithRetry(() => import('./pages/internal/Playbook'));
const OnePager = lazyWithRetry(() => import('./pages/internal/OnePager'));
const DigitalMarketingOffers = lazyWithRetry(() => import('./pages/internal/DigitalMarketingOffers'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));

// Define routes
const router = createBrowserRouter([
  // Root dashboard and all admin routes (restore original structure)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <DynamicLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <ResponsiveDashboard />,
      },
      {
        path: 'inventory',
        element: <Suspense fallback={<LoadingSpinner />}><InventoryEnhanced /></Suspense>,  // Using enhanced version with bulk actions
      },
      {
        path: 'inventory-basic',
        element: <Suspense fallback={<LoadingSpinner />}><Inventory /></Suspense>,  // Keep basic version as alternative
      },
      {
        path: 'inventory/add',
        element: <Suspense fallback={<LoadingSpinner />}><AddVehicle /></Suspense>,
      },
      {
        path: 'inventory/:id',
        element: <Suspense fallback={<LoadingSpinner />}><VehicleDetail /></Suspense>,
      },
      {
        path: 'inventory/:id/print',
        element: <Suspense fallback={<LoadingSpinner />}><VehiclePrintPage /></Suspense>,
      },
      {
        path: 'inventory/:id/edit',
        element: <Suspense fallback={<LoadingSpinner />}><AddVehicle /></Suspense>,
      },
      {
        path: 'ppsr-reports',
        element: <Suspense fallback={<LoadingSpinner />}><PPSRReports /></Suspense>,
      },
      {
        path: 'consignment-vehicles',
        element: <Suspense fallback={<LoadingSpinner />}><ConsignmentVehicles /></Suspense>,
      },
      {
        path: 'expenses',
        element: <Suspense fallback={<LoadingSpinner />}><Expenses /></Suspense>,
      },
      {
        path: 'overhead-expenses',
        element: <Suspense fallback={<LoadingSpinner />}><OverheadExpenses /></Suspense>,
      },
      {
        path: 'customers',
        element: <Suspense fallback={<LoadingSpinner />}><Customers /></Suspense>,
      },
      {
        path: 'customers/:id',
        element: <Suspense fallback={<LoadingSpinner />}><CustomerDetailEnhanced /></Suspense>,
      },
      {
        path: 'customers/:id/edit',
        element: <Suspense fallback={<LoadingSpinner />}><EditCustomer /></Suspense>,
      },
      {
        path: 'customers/:id/test-drive',
        element: <Suspense fallback={<LoadingSpinner />}><ScheduleTestDrive /></Suspense>,
      },
      {
        path: 'customers/:id/vehicle-interest',
        element: <Suspense fallback={<LoadingSpinner />}><AddVehicleInterest /></Suspense>,
      },
      {
        path: 'sales',
        element: <Suspense fallback={<LoadingSpinner />}><Sales /></Suspense>,
      },
      {
        path: 'sales/add',
        element: <Suspense fallback={<LoadingSpinner />}><AddDealApple /></Suspense>,
      },
      {
        path: 'sales/new',
        element: <Suspense fallback={<LoadingSpinner />}><AddDealApple /></Suspense>,
      },
      {
        path: 'sales/:id/edit',
        element: <Suspense fallback={<LoadingSpinner />}><AddDealApple /></Suspense>,
      },
      {
        path: 'sales/:id',
        element: <Suspense fallback={<LoadingSpinner />}><DealDetailNew /></Suspense>,
      },
      {
        path: 'documents',
        element: <Suspense fallback={<LoadingSpinner />}><Documents /></Suspense>,
      },
      {
        path: 'dealings-register',
        element: <Suspense fallback={<LoadingSpinner />}><DealingsRegister /></Suspense>,
      },
      {
        path: 'log-of-changes',
        element: <Suspense fallback={<LoadingSpinner />}><LogOfChanges /></Suspense>,
      },
      {
        path: 'registration-transfer',
        element: <Suspense fallback={<LoadingSpinner />}><RegistrationTransfer /></Suspense>,
      },
      {
        path: 'bulk-pricing-update',
        element: <Suspense fallback={<LoadingSpinner />}><BulkPricingUpdate /></Suspense>,
      },
      {
        path: 'registration-renewal',
        element: <Suspense fallback={<LoadingSpinner />}><RegistrationRenewal /></Suspense>,
      },
      {
        path: 'dealer-licences',
        element: <Suspense fallback={<LoadingSpinner />}><DealerLicences /></Suspense>,
      },
      {
        path: 'subscription',
        element: <Suspense fallback={<LoadingSpinner />}><Subscription /></Suspense>,
      },
      {
        path: 'bank-accounts',
        element: <Suspense fallback={<LoadingSpinner />}><BankAccounts /></Suspense>,
      },
      {
        path: 'contacts',
        element: <Suspense fallback={<LoadingSpinner />}><Contacts /></Suspense>,
      },
      {
        path: 'toll-notice',
        element: <Suspense fallback={<LoadingSpinner />}><TollNotice /></Suspense>,
      },
      {
        path: 'accounting',
        element: <Suspense fallback={<LoadingSpinner />}><Accounting /></Suspense>,
      },
      {
        path: 'government-payments',
        element: <Suspense fallback={<LoadingSpinner />}><GovernmentPayments /></Suspense>,
      },
      {
        path: 'purchase-orders',
        element: <Suspense fallback={<LoadingSpinner />}><PurchaseOrders /></Suspense>,
      },
      {
        path: 'purchase-orders/:id',
        element: <Suspense fallback={<LoadingSpinner />}><PurchaseOrderDetail /></Suspense>,
      },
      {
        path: 'commission',
        element: <Suspense fallback={<LoadingSpinner />}><Commission /></Suspense>,
      },
      {
        path: 'workshop',
        element: <Suspense fallback={<LoadingSpinner />}><Workshop /></Suspense>,
      },
      {
        path: 'reports',
        element: <Suspense fallback={<LoadingSpinner />}><Reports /></Suspense>,
      },
      {
        path: 'standard-reports',
        element: <Suspense fallback={<LoadingSpinner />}><StandardReportsPage /></Suspense>,
      },
      {
        path: 'management-reports',
        element: <Suspense fallback={<LoadingSpinner />}><ManagementReportsPage /></Suspense>,
      },
      {
        path: 'settings',
        element: <Suspense fallback={<LoadingSpinner />}><Settings /></Suspense>,
      },
      {
        path: 'help',
        element: <Suspense fallback={<LoadingSpinner />}><Help /></Suspense>,
      },
      {
        path: 'tips-and-posts',
        element: <Suspense fallback={<LoadingSpinner />}><TipsAndPosts /></Suspense>,
      },
      {
        path: 'getting-started',
        element: <Suspense fallback={<LoadingSpinner />}><GettingStarted /></Suspense>,
      },
      {
        path: 'advertising',
        element: <Suspense fallback={<LoadingSpinner />}><Advertising /></Suspense>,
      },
      {
        path: 'tasks',
        element: <Suspense fallback={<LoadingSpinner />}><Tasks /></Suspense>,
      },
      {
        path: 'tasks-simple',
        element: <Suspense fallback={<LoadingSpinner />}><TasksSimple /></Suspense>,
      },
      {
        path: 'dispatch',
        element: <Suspense fallback={<LoadingSpinner />}><DealerDispatch /></Suspense>,
      },
      {
        path: 'jobs',
        element: <Suspense fallback={<LoadingSpinner />}><DealerDispatchEnhanced /></Suspense>,
      },
      {
        path: 'jobs-simple',
        element: <Suspense fallback={<LoadingSpinner />}><JobsApple /></Suspense>,
      },
      {
        path: 'credits',
        element: <Suspense fallback={<LoadingSpinner />}><Credits /></Suspense>,
      },
      {
        path: 'sourcing',
        element: <Suspense fallback={<LoadingSpinner />}><Sourcing /></Suspense>,
      },
      {
        path: 'intelligence',
        element: <Suspense fallback={<LoadingSpinner />}><IntelligenceHubPalantir /></Suspense>,
      },
      {
        path: 'ai',
        element: <Suspense fallback={<LoadingSpinner />}><DealerAIClaude /></Suspense>,
      },
      {
        path: 'dialer',
        element: <Suspense fallback={<LoadingSpinner />}><Dialer /></Suspense>,
      },
      {
        path: 'inbox',
        element: <Suspense fallback={<LoadingSpinner />}><Inbox /></Suspense>,
      },
      {
        path: 'test-inventory',
        element: <Suspense fallback={<LoadingSpinner />}><TestInventory /></Suspense>,
      },
      {
        path: 'parts',
        element: <Suspense fallback={<LoadingSpinner />}><PartsFinder /></Suspense>,
      },
      {
        path: 'calendar',
        element: <Suspense fallback={<LoadingSpinner />}><Calendar /></Suspense>,
      },
      {
        path: 'sales-tools',
        element: <Suspense fallback={<LoadingSpinner />}><SalesTools /></Suspense>,
      },
      {
        path: 'profile',
        element: <Suspense fallback={<LoadingSpinner />}><UserProfile /></Suspense>,
      },
      {
        path: 'autograb-test',
        element: <Suspense fallback={<LoadingSpinner />}><AutograbTest /></Suspense>,
      },
      {
        path: 'autograb-integration-test',
        element: <Suspense fallback={<LoadingSpinner />}><AutograbTestPage /></Suspense>,
      },
      {
        path: 'migrations',
        element: <Suspense fallback={<LoadingSpinner />}><MigrationHub /></Suspense>,
      },
      {
        path: 'migrations/virtualyard',
        element: <Suspense fallback={<LoadingSpinner />}><VirtualYardMigration /></Suspense>,
      },
      {
        path: 'migrations/easycars',
        element: <Suspense fallback={<LoadingSpinner />}><EasyCarsMigration /></Suspense>,
      },
      {
        path: 'migrations/excel',
        element: <Suspense fallback={<LoadingSpinner />}><ExcelMigration /></Suspense>,
      },
      {
        path: 'easycars-docs',
        element: <Suspense fallback={<LoadingSpinner />}><EasyCarsDocumentation /></Suspense>,
      },
      {
        path: 'admin/backups',
        element: <Suspense fallback={<LoadingSpinner />}><BackupManager /></Suspense>,
      },
      {
        path: 'admin/token-usage',
        element: <Suspense fallback={<LoadingSpinner />}><AdminTokenUsage /></Suspense>,
      },
    ],
  },

  // Internal admin routes (DealerOS team only)
  {
    path: '/internal',
    element: (
      <ProtectedRoute>
        <SystemAdminRoute>
          <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Box
              sx={{
                bgcolor: '#111',
                color: 'white',
                py: 1.5,
                px: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                position: 'sticky',
                top: 0,
                zIndex: 50,
              }}
            >
              <Typography variant="h6" fontWeight={800} sx={{ mr: 2 }}>
                DealerOS
              </Typography>
              {[
                { label: 'Dashboard', path: '/internal' },
                { label: 'Sign Templates', path: '/internal/sign-templates' },
                { label: 'Playbook', path: '/internal/playbook' },
                { label: 'One-Pager', path: '/internal/one-pager' },
                { label: 'Marketing Offer', path: '/internal/marketing-offer' },
                { label: 'Calculator', path: '/internal/calculator' },
                { label: 'Landing', path: '/home' },
                { label: 'Pricing', path: '/pricing' },
                { label: 'Submissions', path: '/internal/onboarding-submissions' },
              ].map(link => (
                <Box
                  key={link.path}
                  component="a"
                  href={link.path}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    transition: 'all 0.15s',
                    '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
            <Box component="main">
              <Outlet />
            </Box>
          </Box>
        </SystemAdminRoute>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingSpinner />}><AdminDashboard /></Suspense>,
      },
      {
        path: 'admin',
        element: <Suspense fallback={<LoadingSpinner />}><AdminDashboard /></Suspense>,
      },
      {
        path: 'sign-templates',
        element: <Suspense fallback={<LoadingSpinner />}><SignTemplatesAdmin /></Suspense>,
      },
      {
        path: 'onboarding-submissions',
        element: <Suspense fallback={<LoadingSpinner />}><OnboardingSubmissions /></Suspense>,
      },
      {
        path: 'calculator',
        element: <Suspense fallback={<LoadingSpinner />}><RepCalculator /></Suspense>,
      },
      {
        path: 'playbook',
        element: <Suspense fallback={<LoadingSpinner />}><Playbook /></Suspense>,
      },
      {
        path: 'one-pager',
        element: <Suspense fallback={<LoadingSpinner />}><OnePager /></Suspense>,
      },
      {
        path: 'marketing-offer',
        element: <Suspense fallback={<LoadingSpinner />}><DigitalMarketingOffers /></Suspense>,
      },
      {
        path: 'pricing',
        element: <Suspense fallback={<LoadingSpinner />}><InternalPricing /></Suspense>,
      },
    ],
  },

  // Public landing page (no auth required)
  {
    path: '/home',
    element: <Suspense fallback={<LoadingSpinner />}><LandingPage /></Suspense>,
    errorElement: <RouteErrorBoundary />,
  },

  // Public pricing page (no auth required)
  {
    path: '/pricing',
    element: <Suspense fallback={<LoadingSpinner />}><PricingPage /></Suspense>,
    errorElement: <RouteErrorBoundary />,
  },

  // Demo marketplace routes (new comprehensive marketplace)
  {
    path: '/demo',
    element: (
      <ErrorBoundary>
        <MarketplaceLayout />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Homepage />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'cars',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <BrowseCars />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'cars/:id',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <MarketplaceVehicleDetail />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'sell',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <SellYourCar />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'finance',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <CarFinance />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'how-it-works',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <HowItWorks />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'contact',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Contact />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'about',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <About />
            </Suspense>
          </ErrorBoundary>
        ),
      },
    ],
    errorElement: (
      <ErrorBoundary fallback={
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Demo Page Error
          </Typography>
          <Typography variant="body1">
            The demo page encountered an error. Please try refreshing or return to the home page.
          </Typography>
        </Box>
      } />
    ),
  },
  
  // Legacy customer-facing routes (keep for backwards compatibility)
  {
    path: '/public',
    element: (
      <ThemeProvider theme={customerThemeLight}>
        <CustomerLayout />
      </ThemeProvider>
    ),
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingSpinner />}><CustomerLanding /></Suspense>,
      },
    ],
  },
  {
    path: '/browse',
    element: (
      <ThemeProvider theme={customerThemeLight}>
        <CustomerLayout />
      </ThemeProvider>
    ),
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingSpinner />}><BrowsePage /></Suspense>,
      },
      {
        path: ':id',
        element: <Suspense fallback={<LoadingSpinner />}><VehicleDetailPage /></Suspense>,
      },
    ],
  },
  {
    path: '/test-drive',
    element: (
      <ThemeProvider theme={customerThemeLight}>
        <CustomerLayout />
      </ThemeProvider>
    ),
    children: [
      {
        index: true,
        element: <Suspense fallback={<LoadingSpinner />}><TestDrivePage /></Suspense>,
      },
    ],
  },
  
  // Test routes
  {
    path: '/test',
    element: <Suspense fallback={<LoadingSpinner />}><SimpleTest /></Suspense>,
  },
  {
    path: '/test-form-protection',
    element: <Suspense fallback={<LoadingSpinner />}><TestFormProtection /></Suspense>,
  },
  {
    path: '/test-modal-protection',
    element: <Suspense fallback={<LoadingSpinner />}><TestModalProtection /></Suspense>,
  },
  {
    path: '/test-auth',
    element: <Suspense fallback={<LoadingSpinner />}><TestConnection /></Suspense>,
  },
  {
    path: '/vehicle-test',
    element: <Suspense fallback={<LoadingSpinner />}><VehicleTest /></Suspense>,
  },
  
  // Auth routes
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/admin/login',
    element: <Login />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/set-password',
    element: <SetPassword />,
  },
  {
    path: '/sign-up',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <SignUpPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },

  // Public signature page — DocuSign-style modal in customer mode
  {
    path: '/sign-deal/:token',
    element: <Suspense fallback={<LoadingSpinner />}><SignDealPage /></Suspense>,
  },
  // Legacy structured-fields wizard (kept as fallback URL while we burn down old links)
  {
    path: '/sign-deal-wizard/:token',
    element: <Suspense fallback={<LoadingSpinner />}><CustomerSignatureWizard /></Suspense>,
  },
  // Original old single-form signing page
  {
    path: '/sign-deal-legacy/:token',
    element: <Suspense fallback={<LoadingSpinner />}><CustomerSignature /></Suspense>,
  },
  {
    path: '/signature/:dealId',
    element: <Suspense fallback={<LoadingSpinner />}><SignDealPage /></Suspense>,
  },

  // Public customer chat — reply via web, no login, no SMS cost
  {
    path: '/chat/:token',
    element: <Suspense fallback={<LoadingSpinner />}><CustomerChat /></Suspense>,
  },
  
  // Xero routes
  {
    path: '/xero/callback',
    element: <Suspense fallback={<LoadingSpinner />}><XeroCallback /></Suspense>,
  },
  {
    path: '/xero/test',
    element: <Suspense fallback={<LoadingSpinner />}><XeroTest /></Suspense>,
  },
  {
    path: '/xero/debug',
    element: <Suspense fallback={<LoadingSpinner />}><XeroDebug /></Suspense>,
  },
  {
    path: '/xero/debug-credentials',
    element: <Suspense fallback={<LoadingSpinner />}><XeroDebugCredentials /></Suspense>,
  },
  {
    path: '/xero/fix',
    element: <Suspense fallback={<LoadingSpinner />}><XeroFix /></Suspense>,
  },
  {
    path: '/test-features',
    element: <Suspense fallback={<LoadingSpinner />}><TestFeatures /></Suspense>,
  },

  // Redirect legacy routes to demo context
  {
    path: '/cars',
    loader: ({ request }) => {
      const url = new URL(request.url);
      const searchParams = url.search;
      return redirect(`/demo/cars${searchParams}`);
    },
  },

  // Catch-all route for 404 handling
  {
    path: '*',
    element: (
      <ErrorBoundary fallback={
        <Box sx={{ p: 4, textAlign: 'center', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper sx={{ p: 4, maxWidth: 600 }}>
            <Typography variant="h4" color="error" gutterBottom>
              Page Not Found
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              The page you're looking for doesn't exist. You might want to try our demo marketplace instead.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => window.location.href = '/'}>
                Go Home
              </Button>
              <Button variant="contained" onClick={() => window.location.href = '/demo'}>
                View Demo
              </Button>
            </Box>
          </Paper>
        </Box>
      } />
    ),
  },

]);

export default router;
